<template>
  <div class="clock-advanced-stats">
    <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 300px;">
      <v-progress-circular indeterminate color="primary" size="56"></v-progress-circular>
    </div>

    <div v-else-if="!statsData" class="d-flex flex-column align-center justify-center" style="min-height: 300px;">
      <v-icon size="72" class="text--disabled">mdi-chart-areaspline</v-icon>
      <p class="text--secondary mt-3">Selecciona un periodo para ver las estadísticas avanzadas</p>
    </div>

    <div v-else>
      <v-row class="mb-4">
        <!-- Temporary Chart Placeholder, will be replaced by alerts chart -->
        <v-col cols="12" md="8">
          <v-card class="elevation-1 pa-4 h-100 rounded-lg">
            <h3 class="text-subtitle-1 font-weight-bold mb-4">
              <v-icon color="primary" class="mr-2">mdi-chart-line</v-icon>
              Evolución de Alertas
            </h3>
            <div style="height: 300px; display: flex; align-items: center; justify-content: center;">
              <!-- Chart goes here -->
              <AlertsChart ref="alertsChart" :chart-data="temporalData" />
            </div>
          </v-card>
        </v-col>

        <!-- General temporal KPIs -->
        <v-col cols="12" md="4">
          <v-row class="ma-n2">
            <v-col cols="12" sm="6" md="12" class="pa-2">
              <v-card class="elevation-1 pa-3 rounded-lg d-flex align-center h-100">
                <v-icon large color="info" class="mr-3">mdi-fingerprint</v-icon>
                <div>
                  <div class="text-subtitle-2 text--secondary">Total Fichajes</div>
                  <div class="text-h5 font-weight-bold">{{ statsData.totalEntries }}</div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="12" class="pa-2">
              <v-card class="elevation-1 pa-3 rounded-lg d-flex align-center h-100">
                <v-icon large color="success" class="mr-3">mdi-calculator</v-icon>
                <div>
                  <div class="text-subtitle-2 text--secondary">Promedio Fichajes / Día</div>
                  <div class="text-h5 font-weight-bold">{{ avgEntriesPerDay }}</div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="12" class="pa-2">
              <v-card class="elevation-1 pa-3 rounded-lg d-flex align-center h-100">
                <v-icon large color="secondary" class="mr-3">mdi-alert</v-icon>
                <div>
                  <div class="text-subtitle-2 text--secondary">Total Alertas en el Periodo</div>
                  <div class="text-h5 font-weight-bold">{{ statsData.totalAlerts }}</div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="12" class="pa-2">
              <v-card class="elevation-1 pa-3 rounded-lg d-flex align-center h-100">
                <v-icon large color="primary" class="mr-3">mdi-clock-fast</v-icon>
                <div>
                  <div class="text-subtitle-2 text--secondary">Promedio de Alertas / Día</div>
                  <div class="text-h5 font-weight-bold">{{ avgAlertsPerDay }}</div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- Resumen por fechas -->
      <v-card elevation="1" class="mt-6">
        <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
          <v-icon color="primary" class="mr-2">mdi-calendar-multiselect</v-icon>
          Resumen Temporal
        </v-card-title>
        <v-divider></v-divider>
        <v-simple-table>
          <thead>
            <tr>
              <th>Fecha / Semana</th>
              <th>Fichajes</th>
              <th>Alertas Generadas</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in temporalData" :key="d.date">
              <td>
                <div class="font-weight-medium">{{ d.label }}</div>
              </td>
              <td>{{ d.entriesCount }}</td>
              <td>
                <v-chip x-small :color="d.alertsCount > 0 ? 'secondary' : 'primary'" dark>
                  {{ d.alertsCount }}
                </v-chip>
              </td>
            </tr>
            <tr v-if="temporalData.length === 0">
              <td colspan="3" class="text-center text--secondary py-4">No hay datos en el periodo.</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import clockEntryService from '@/services/clockentry.service';
import AlertsChart from '@/components/Charts/AlertsChart.vue';
import moment from 'moment';

export default {
  name: 'clock-advanced-stats',
  components: { AlertsChart },
  props: {
    dateFrom: { type: String, default: null },
    dateTo: { type: String, default: null },
    workcenterId: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: false,
      statsData: null,
      temporalData: [],
    };
  },
  computed: {
    avgAlertsPerDay() {
      if (!this.temporalData || this.temporalData.length === 0) return 0;
      return (this.statsData.totalAlerts / this.temporalData.length).toFixed(1);
    },
    avgEntriesPerDay() {
      if (!this.temporalData || this.temporalData.length === 0) return 0;
      return (this.statsData.totalEntries / this.temporalData.length).toFixed(1);
    },
  },
  mounted() {
    if (this.dateFrom && this.dateTo && this.workcenterId) {
      this.loadStats();
    }
  },
  watch: {
    dateFrom() { this.loadStats(); },
    dateTo() { this.loadStats(); },
  },
  methods: {
    async loadStats() {
      if (!this.dateFrom || !this.dateTo || !this.workcenterId) return;
      this.loading = true;
      this.statsData = null;
      this.temporalData = [];
      if (this.$refs.alertsChart) {
        this.$refs.alertsChart.updateChartData([]);
      }
      try {
        const response = await clockEntryService.getWorkersClockEntriesInRange(this.workcenterId, this.dateFrom, this.dateTo);
        const mapped = response.map((item) => ({
          ...item,
          time: clockEntryService.unixTimeToDateTime(item.unixClockInTime),
          timeLocale: clockEntryService.unixTimeToLocaleDateTime(item.unixClockInTime),
          sourceType: item.type,
          workerName: item.worker.name,
          workerLastName: item.worker.lastName1,
          worker: item.worker,
        }));

        const groupedEntriesFlatted = clockEntryService.flattenGroupedEntries(clockEntryService.getGroupedEntriesForAllWorkers(mapped));

        let totalAlerts = 0;

        // Group by day for accurate timeline
        const groupedByDay = {};

        groupedEntriesFlatted.forEach((dayData) => {
          const dateStr = dayData.time; // YYYY-MM-DD
          if (!groupedByDay[dateStr]) {
            groupedByDay[dateStr] = {
              date: dateStr,
              label: clockEntryService.getStringDate(dateStr),
              entriesCount: 0,
              alertsCount: 0,
            };
          }

          const dayAlertsCount = clockEntryService.setDayAlerts(
            dayData.alertDayStartWithClockOut,
            dayData.alertDayEndWithClockIn,
            dayData.alertDayWithManualEntries,
            dayData.alertDayWithAdminEntries,
            dayData.alertDayWithUnequalEntries,
            dayData.alertDayWithConsecutivesSameTypes,
          ).length;

          groupedByDay[dateStr].entriesCount += dayData.entries ? dayData.entries.length : 0;
          groupedByDay[dateStr].alertsCount += dayAlertsCount;
          totalAlerts += dayAlertsCount;
        });

        // Convert the map to an array and sort by date ascending
        this.temporalData = Object.values(groupedByDay).sort((a, b) => moment(a.date).diff(moment(b.date)));

        this.statsData = {
          totalEntries: mapped.length,
          totalAlerts,
        };

        this.$nextTick(() => {
          if (this.$refs.alertsChart) {
            this.$refs.alertsChart.updateChartData(this.temporalData);
          }
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.clock-advanced-stats {
  width: 100%;
}
</style>
